import { Box, Typography } from '@material-ui/core'
import {
  integrationColors,
  integrationNames,
  storeIntegrationKeyByType,
  IntegrationTypeKey
} from '../../../../types/integrationTypeUtils'
import { makeStyles } from '@material-ui/core/styles'
import { RoundedCheckbox } from '../../../shared/ui/roundedCheckbox'
import { Controller, useFormContext } from 'react-hook-form'
import camelCase from 'lodash/camelCase'
import { IntegrationType } from '../../../../server/mpsklad_core/Entity/IntegrationType'
import { useLazyEffect } from '../../../../hooks/commonHooks'
import { useStore } from '../../../../hooks/storeHook'
import { observer } from 'mobx-react-lite'

// NOTE: This component is tightly coupled with ProductForm and is intended to be used strictly within its form context.
// It relies on the field structure and naming conventions defined in ProductForm.
// Therefore, it is located inside the form widget in the ui folder.
interface IntegrationSelectorProps {
  readOnlyMarkets?: IntegrationType[] // маркетплейсы, которые нельзя редактировать
}

export const IntegrationSelector = observer(({ readOnlyMarkets }: IntegrationSelectorProps) => {
  const classes = useStyles()

  const {
    syncStore: {
      ozonAccounts,
      wbAccounts,
      yandexMarketAccounts
    }
  } = useStore()

  const text =
    readOnlyMarkets && readOnlyMarkets.length > 0
      ? "На какой маркет перенести карточки"
      : "Для какого маркета создавать атрибуты в базовой карточке:";


  return (
    <Box display={'flex'} className={classes.wrapper} alignItems={'center'}>
      <Typography className={classes.label}>{text}</Typography>
      <Box className={classes.checkboxContainer}>
        {ozonAccounts.length > 0 && (
          <IntegrationCheckbox
            type={storeIntegrationKeyByType[IntegrationType.Ozon]}
            readOnly={readOnlyMarkets?.includes(IntegrationType.Ozon)}
          />
        )}
        {wbAccounts.length > 0 && (
          <IntegrationCheckbox
            type={storeIntegrationKeyByType[IntegrationType.Wildberries]}
            readOnly={readOnlyMarkets?.includes(IntegrationType.Wildberries)}
          />
        )}
        {yandexMarketAccounts.length > 0 && (
          <IntegrationCheckbox
            type={storeIntegrationKeyByType[IntegrationType.YandexMarket]}
            readOnly={readOnlyMarkets?.includes(IntegrationType.YandexMarket)}
          />
        )}
      </Box>
    </Box>
  )
})

interface IntegrationCheckboxProps {
  type: IntegrationTypeKey
  readOnly?: boolean
}

const IntegrationCheckbox = ({ type, readOnly }: IntegrationCheckboxProps) => {
  const classes = useStyles()
  const { control, watch, setValue } = useFormContext()
  const fieldName = `${camelCase(type)}.include`
  const categoryFieldName = `${camelCase(type)}.category`
  const includeValue = watch(fieldName)

  useLazyEffect(() => {
    if (!includeValue) setValue(categoryFieldName, null)
  }, [includeValue])

  return (
    <Box display={'flex'} className={classes.checkbox}>
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => (
          <RoundedCheckbox
            {...field}
            color={integrationColors[type]}
            size="small"
            disableRipple
            checked={field.value ?? false}
            disabled={readOnly}
            style={{ opacity: readOnly ? 0.5 : 1 }} // делаем полупрозрачным, если readOnly
          />
        )}
      />
      <Typography
        className={classes.integration}
        style={{ color: readOnly ? '#888' : 'inherit' }} // серый текст для readOnly
      >
        {integrationNames[type]}</Typography>
    </Box>
  )
}

const useStyles = makeStyles({
  label: {
    fontSize: 13
  },
  wrapper: {
    gap: 15,
    marginTop: 15,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  integration: {
    marginLeft: 4,
    fontSize: 13
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  checkbox: {
    gap: 3,
    alignItems: 'center'
  }
})
